import { Divider, Spin } from 'antd';
import styles from './index.module.scss';
import { useEffect, useMemo, useState } from 'react';
import { Button, Empty, Input, Tag, Tree } from '@maxtropy/components';
import { DataNode } from 'antd/es/tree';
import {
  apiV2CircuitPowerRoomTreePost,
  apiV2DistributionCabinetGetPowerRoomByCircuitIdPost,
} from '@maxtropy/dmes-apis-v2';
import { ApartmentOutlined } from '@ant-design/icons';
import { levelDisplay } from '../../utils';
import roomImg from '../../imgs/room.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { isNil } from 'lodash-es';
import { useHasPermission } from '@/utils/hooks';
import { PermissionsType } from '@/common/permissionsConst';

interface Props {
  setRoomId: (v?: number) => void;
  setRoomName: (v?: string) => void;
  setLevel?: (v: number) => void;
}

interface NewDataNode {
  name: string;
  id: number;
  parentId: number;
  level: number;
  disabled: boolean;
  children?: NewDataNode[];
}

// 扩展DataNode类型，添加自定义属性
interface CustomDataNode extends DataNode {
  dataRef: NewDataNode; // 存储原始节点数据
}

const StationTree: React.FC<Props> = ({ setRoomId, setRoomName, setLevel }) => {
  const [data, setData] = useState<NewDataNode[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchVal, setSearchVal] = useState<string>();
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState<boolean>(true);
  const navigate = useNavigate();
  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const url_circuitId = urlSearchParams.get('circuitId') || undefined;
  const hasViewPermission = useHasPermission(PermissionsType.B_OVERALLTOPOLOGYMAP);

  // 获取并转换树结构
  useEffect(() => {
    setLoading(true);

    apiV2CircuitPowerRoomTreePost({})
      .then((res: any) => {
        const transformTreeData = (list: any[], parentId: number = 0): NewDataNode[] => {
          return list.map(item => ({
            name: item.roomName,
            id: item.roomId,
            parentId,
            disabled: item.disable,
            level: item.level,
            children: item.children ? transformTreeData(item.children, item.roomId) : [],
          }));
        };

        const formatted = transformTreeData(res?.list ?? []);
        setData(formatted);

        setRoomId(formatted?.[0].id);
        setRoomName(formatted?.[0].name);
        setLevel?.(formatted?.[0].level);
        setSelectedKeys(isNil(formatted?.[0].id) ? [] : [formatted?.[0]?.id]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (isNil(url_circuitId) || data.length === 0) return;

    apiV2DistributionCabinetGetPowerRoomByCircuitIdPost({ id: url_circuitId }).then(res => {
      const roomId = res.value;
      if (isNil(roomId)) return;

      if (roomId === -999) {
        setRoomId(undefined);
        setRoomName(undefined);
        setLevel?.(0);
        setSelectedKeys([]);
        return;
      }

      // 遍历查找匹配的节点及其父级路径
      const findNodeAndPath = (
        nodes: NewDataNode[],
        targetId: number,
        path: number[] = []
      ): { node?: NewDataNode; path: number[] } => {
        for (const node of nodes) {
          const currentPath = [...path, node.id];
          if (Number(node.id) === targetId) {
            return { node, path: currentPath };
          }
          if (node.children?.length) {
            const result = findNodeAndPath(node.children, targetId, currentPath);
            if (result.node) return result;
          }
        }
        return { path: [] };
      };

      const { node, path } = findNodeAndPath(data, roomId);

      if (node) {
        setRoomId(node.id);
        setRoomName(node.name);
        setLevel?.(node.level);
        setSelectedKeys([node.id]);
        setExpandedKeys(path); // 展开到该节点路径
        setAutoExpandParent(true);
      }
    });
  }, [url_circuitId, data]);

  const getAllKeys = (nodes: NewDataNode[]): React.Key[] => {
    let keys: React.Key[] = [];
    nodes.forEach(node => {
      keys.push(node.id);
      if (node.children && node.children.length > 0) {
        keys = keys.concat(getAllKeys(node.children));
      }
    });
    return keys;
  };

  // 构造用于 Tree 组件的 treeData，支持搜索高亮
  const treeData: CustomDataNode[] = useMemo(() => {
    const loop = (data: NewDataNode[]): CustomDataNode[] =>
      data.map(item => {
        const strTitle = item.name;
        const searchName = searchVal ?? '';
        const index = strTitle.indexOf(searchName);
        const beforeStr = strTitle.substring(0, index);
        const afterStr = strTitle.slice(index + searchName.length);
        const title = (
          <div className={styles.title}>
            <span className={styles.titleText}>
              {index > -1 ? (
                <>
                  {beforeStr}
                  <span className={styles.filterTxt}>{searchName}</span>
                  {afterStr}
                </>
              ) : (
                strTitle
              )}
            </span>
            <Tag border="solid" color="default">
              {levelDisplay[item.level]}
            </Tag>
          </div>
        );

        return {
          key: item.id,
          title,
          level: item.level,
          disabled: item.disabled,
          icon: <img src={roomImg} style={{ marginBottom: 4, width: 16, height: 16 }} alt="" />,
          children: item.children && item.children.length > 0 ? loop(item.children) : [],
          dataRef: item, // 存储原始节点数据
        };
      });
    return loop(data);
  }, [searchVal, data]);

  // 根据搜索值自动展开节点
  useEffect(() => {
    const searchName = searchVal ?? '';
    const matchedKeys: React.Key[] = [];

    const loop = (nodes: NewDataNode[]) => {
      for (const node of nodes) {
        if (node.name.includes(searchName)) {
          matchedKeys.push(node.id);
        }
        if (node.children?.length) {
          loop(node.children);
        }
      }
    };

    loop(data);
    setExpandedKeys(matchedKeys);
    setAutoExpandParent(true);
  }, [searchVal, data]);

  const allKeys = useMemo(() => getAllKeys(data), [data]);

  const isAllExpanded = expandedKeys.length > 0 && expandedKeys.length === allKeys.length;

  const clearUrlParams = () => {
    const params = new URLSearchParams(search);
    if (params.has('circuitId')) {
      navigate(`/dmes/circuit/topologicalGraph`, { replace: true }); // 使用 replace 避免堆叠历史记录
    }
  };

  return (
    <div className={styles.treeArea} style={{ width: '100%' }}>
      <div className={styles.searchArea}>
        <Input.Search
          placeholder="请输入名称"
          allowClear
          onSearch={v => {
            setSearchVal(v);
          }}
        />
        <Divider style={{ margin: '16px 0 0px' }} />
      </div>
      <div className={styles.treeBox}>
        <Spin spinning={loading}>
          <Button
            wrapClassName={styles.btnWrap}
            onClick={() => {
              setExpandedKeys(isAllExpanded ? [] : allKeys);
              setAutoExpandParent(false);
            }}
          >
            {isAllExpanded ? '收起' : '展开'}全部站房
          </Button>
          {hasViewPermission ? (
            <div
              className={`${styles.headerRow} ${!selectedKeys.length ? styles.active : styles.inActive}`}
              onClick={() => {
                clearUrlParams();
                setRoomId(undefined);
                setRoomName(undefined);
                setSelectedKeys([]);
                setLevel?.(0);
              }}
            >
              <ApartmentOutlined className={styles.icon} />
              整体拓扑图
              <Tag border="solid" color="default" style={{ marginLeft: 6 }}>
                总拓扑
              </Tag>
            </div>
          ) : null}

          {treeData.length > 0 ? (
            <Tree
              style={{ height: 'calc(100vh - 288px)', overflow: 'auto', marginLeft: hasViewPermission ? 16 : 0 }}
              blockNode
              showIcon
              treeData={treeData}
              selectedKeys={selectedKeys}
              onSelect={(keys, info) => {
                clearUrlParams();
                const nodeData = (info.node as unknown as CustomDataNode).dataRef;
                setRoomId(nodeData.id);
                setRoomName(nodeData.name);
                setLevel?.(nodeData.level);
                setSelectedKeys(keys);
              }}
              expandedKeys={expandedKeys}
              onExpand={v => {
                setExpandedKeys(v);
                setAutoExpandParent(false);
              }}
              autoExpandParent={autoExpandParent}
            />
          ) : (
            <Empty style={{ marginTop: 40 }} />
          )}
        </Spin>
      </div>
    </div>
  );
};

export default StationTree;
